import { BaseFilter } from '@/_store/offers/offers.types';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, computed, input, output, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subject, map, merge, takeUntil } from 'rxjs';

export interface CheckboxFilterValue {
	id: number;
	value: boolean;
}

export enum CheckboxSelected {
	ALL = 'all',
	SOME = 'some',
	NONE = 'none',
}

interface Search {
	show: boolean;
	options?: {
		placeholder?: string;
	};
}

@Component({
	selector: 'app-multi-choice',
	templateUrl: './multi-choice.component.html',
	styleUrls: ['./multi-choice.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiChoiceComponent implements OnDestroy {
	showCheckAll = input(true);
	search = input<Search>({
		show: false,
	});
	disabled = input(false);
	enableScroll = input(true);
	constrainScrollHeight = input(false);
	collapsedVisibleCount = input(10);

	showAllOptions = signal(false);
	options = signal<BaseFilter[]>([]);

	readonly visibleOptions = computed(() => {
		const opts = this.options();
		if (this.enableScroll() || this.showAllOptions() || !opts.length) {
			return opts;
		}

		return opts.slice(0, this.collapsedVisibleCount());
	});

	readonly hasMoreOptions = computed(
		() => !this.enableScroll() && !this.showAllOptions() && this.options().length > this.collapsedVisibleCount(),
	);

	@Input() set _options(options: BaseFilter[]) {
		if (!Array.isArray(options)) {
			return;
		}

		const newOptionIds = options
			.map((o) => o.id.toString())
			.sort()
			.join(',');
		const currentOptionIds = this.checkboxesForm ? Object.keys(this.checkboxesForm.controls).sort().join(',') : '';

		this.options.set(options);
		this.showAllOptions.set(false);

		if (!this.checkboxesForm || currentOptionIds !== newOptionIds) {
			this.createCheckboxesControls(options);
			if (Array.isArray(this.selected)) {
				this.applySelected(this.selected);
			}
		}

		this.updateToggleAllCheckboxState();
	}

	selected: number[];
	@Input() set _selected(selected: number[]) {
		if (!Array.isArray(selected)) {
			return;
		}

		this.selected = selected;
		if (!this.checkboxesForm) {
			return;
		}

		this.applySelected(selected);
		this.updateToggleAllCheckboxState();
	}

	filterValue = output<CheckboxFilterValue>();
	searchUpdated = output<string>();

	toggleAllCheckbox$: BehaviorSubject<CheckboxSelected> = new BehaviorSubject<CheckboxSelected>(
		CheckboxSelected.NONE,
	);
	checkboxesForm: FormGroup;
	CheckboxSelected = CheckboxSelected;

	private readonly destroy$: Subject<void> = new Subject<void>();

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	showMore(): void {
		this.showAllOptions.set(true);
	}

	toggleAll(checked: boolean): void {
		Object.keys(this.checkboxesForm.controls).forEach((key) => {
			if (this.filteredControlValues.find((v) => v.id === key)) {
				this.checkboxesForm.get(key)?.patchValue(checked);
			}
		});
	}

	private createCheckboxesControls(baseFilterArr: BaseFilter[]): void {
		if (!baseFilterArr?.length) {
			return;
		}
		this.checkboxesForm = new FormGroup({});
		baseFilterArr.forEach(({ id }) => {
			this.checkboxesForm.addControl(
				id.toString(),
				new FormControl({
					value: false,
					disabled: false,
				}),
			);
		});

		merge(
			...this.options().map((option) =>
				this.checkboxesForm.get(option.id.toString()).valueChanges.pipe(
					map((value) => ({
						id: option.id,
						value,
					})),
				),
			),
		)
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				this.filterValue.emit(value);
				this.updateToggleAllCheckboxState();
			});
	}

	private applySelected(selected: number[]): void {
		this.options().forEach((option) => {
			this.checkboxesForm.get(option.id.toString())?.setValue(selected.includes(option.id), { emitEvent: false });
		});
	}

	private get filteredControlValues(): { id: string; value: boolean }[] {
		let controlValues: { id: string; value: boolean }[] = [];

		Object.keys(this.checkboxesForm.controls).forEach((key) => {
			if (this.options().find((v) => v.id === +key)) {
				controlValues = [...controlValues, { id: key, value: this.checkboxesForm.controls[key].value }];
			}
		});
		return controlValues;
	}

	private updateToggleAllCheckboxState(): void {
		if (!this.checkboxesForm) return;

		const areAllControlsChecked = this.filteredControlValues.every((control) => control.value);
		const anyControlChecked = this.filteredControlValues.some((control) => control.value);

		let checkboxState: CheckboxSelected = CheckboxSelected.NONE;
		if (areAllControlsChecked && this.filteredControlValues.length > 0) {
			checkboxState = CheckboxSelected.ALL;
		} else if (anyControlChecked) {
			checkboxState = CheckboxSelected.SOME;
		}

		this.toggleAllCheckbox$.next(checkboxState);
	}
}
